import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, setToken, getToken } from "@/lib/api";
import { useSiteSettings } from "@/lib/settings";
import { toast } from "sonner";
import { useSEO } from "@/lib/seo";
import Captcha from "@/components/site/Captcha";

export default function AdminLogin() {
  useSEO({ title: "Admin Login", noIndex: true });
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const captchaRef = useRef(null);
  const { settings } = useSiteSettings();

  useEffect(() => {
    if (getToken()) {
      api.get("/admin/auth/me")
        .then(() => nav("/admin/dashboard", { replace: true }))
        .catch(() => setToken(null));
    }
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { captcha_token, captcha_answer } = captchaRef.current?.getValues() || {};
      const r = await api.post("/admin/auth/login", { email, password, captcha_token, captcha_answer });
      setToken(r.data.access_token);
      toast.success("Welcome back");
      nav("/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
      captchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen md:grid-cols-2">
      <div className="hidden bg-gradient-to-br from-primary via-primary to-[#1e5a8a] p-12 text-white md:flex md:flex-col md:justify-between">
        <div className="flex items-center gap-3">
          <span className="logo-mark-dark inline-flex h-14 w-14 items-center justify-center">
            <img src={settings.logo_url} alt="Brand logo" className="h-full w-full rounded-md object-cover" />
          </span>
          <div className="font-heading text-xl font-bold">
            <span>{settings.brand_left}</span>
            <span className="text-secondary">{settings.brand_right}</span>
          </div>
        </div>
        <div>
          <h1 className="font-heading text-4xl font-bold leading-tight">Admin Console</h1>
          <p className="mt-4 max-w-md text-white/85">
            Manage jobs, applications, candidates and the entire PharmacoHires workflow in one place.
          </p>
        </div>
        <div className="text-xs text-white/70">© {new Date().getFullYear()} {settings.brand_left}{settings.brand_right}</div>
      </div>

      <div className="flex items-center justify-center bg-slate-50 p-6">
        <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <h2 className="font-heading text-2xl font-semibold text-slate-900">Sign in</h2>
          <p className="mt-1 text-sm text-slate-500">Enter your admin credentials to continue.</p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-login-form">
            <div>
              <Label htmlFor="al-email">Email</Label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="al-email" type="email" required className="pl-9" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" />
              </div>
            </div>
            <div>
              <Label htmlFor="al-pwd">Password</Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="al-pwd" type="password" required className="pl-9" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" />
              </div>
            </div>
            <Captcha ref={captchaRef} testId="admin-login-captcha" />
            <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90" data-testid="admin-login-submit">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
